<template>
    <form class="form-register">

        <div class="form-group">
            <p class="subheading mb-2">{{ translate.home.chooseSignUpMethod }}</p>
            <div class="registration-method-toggle mb-3">
                <button type="button"
                        :class="{'active': registerVia === 'phone'}"
                        @click.prevent="registerVia = 'phone'">
                    {{ translate.account.placeholder.phone }}
                </button>
                <button type="button"
                        :class="{'active': registerVia === 'email'}"
                        @click.prevent="registerVia = 'email'">
                    {{ translate.login.emailAddress }}
                </button>
            </div>

            <div v-if="registerVia === 'phone'">
                <span class="error-input-container"
                      :class="{'has-error': ($v.phoneNumber.$error || formSubmitted) && !$v.phoneNumber.required}">
                    <div class="d-flex align-items-center gap-2">
                        <select class="form-control-v2"
                                style="width: auto; flex-shrink: 0; padding: 21px 36px 21px 16px;"
                                v-model="countryCodeId">
                            <option v-for="country in countryCodes" :value="country.id" :key="country.id">
                                {{ country.emoji }} {{ country.dialCode }}
                            </option>
                        </select>
                        <input class="form-control-v2"
                               style="flex: 1;"
                               :placeholder="translate.login.phonePlaceholder"
                               type="tel"
                               v-model.trim="$v.phoneNumber.$model">
                    </div>
                </span>

                <span v-if="$v.phoneNumber.$error || formSubmitted">
                    <error-component v-if="!$v.phoneNumber.required"
                                     :message="translate.login.phoneRequired"></error-component>
                    <error-component v-if="phoneNotValid"
                                     :message="translate.login.phoneNotValid"></error-component>
                </span>
            </div>

            <div v-if="registerVia === 'email'">
                <span class="error-input-container"
                      :class="{'has-error': ($v.email.$error || formSubmitted) && !$v.email.required}">
                    <input class="form-control-v2"
                           :placeholder="translate.login.emailAddress"
                           type="text"
                           autocomplete="off"
                           v-model.trim="$v.email.$model">
                </span>

                <span v-if="$v.email.$error || formSubmitted">
                    <error-component v-if="!$v.email.required"
                                     :message="translate.login.emailRequired"></error-component>
                    <error-component v-if="emailNotValid"
                                     :message="translate.login.emailNotValid"></error-component>
                </span>
            </div>

            <span v-if="emailOrPhoneTaken">
                <error-component :message="translate.login.emailOrPhoneTaken">
                    <a :href="regionRelativeUrl('/users/forgot-password/')">{{ translate.login.emailOrPhoneTaken2 }}</a>{{ translate.login.emailOrPhoneTaken3 }}
                </error-component>
            </span>
        </div>

        <div class="form-group">
            <span class="error-input-container"
                  :class="{'has-error': ($v.username.$error || formSubmitted)
                  && (!$v.username.required || usernameTaken)}">
                <input class="form-control-v2" :placeholder="translate.login.username"
                       type="text"
                       v-model.trim="$v.username.$model">
            </span>

            <span v-if="$v.username.$error || formSubmitted">
                <error-component :message="translate.login.usernameRequired"
                                 v-if="!$v.username.required"></error-component>
                <error-component :message="translate.login.usernameTaken"
                                 v-if="usernameTaken"></error-component>
            </span>

            <div class="username-suggestions">
                <a href="#" v-for="(username, i) in suggestedUsernames"
                   class="suggested-username" @click.prevent="applySuggestedUsername(username)" :key="i">{{ username }}</a>
            </div>
        </div>

        <div class="form-group">
            <span class="error-input-container"
                  :class="{'has-error': ($v.password.$error || formSubmitted)
                  && !$v.password.required}">
                <input class="form-control-v2" :placeholder="translate.login.password"
                       type="password"
                       v-model.trim="$v.password.$model">
            </span>

            <span v-if="$v.password.$error || formSubmitted">
                <error-component :message="translate.login.passwordRequired"
                                 v-if="!$v.password.required"></error-component>
            </span>
        </div>

        <div class="form-group">
            <span class="error-input-container"
                  :class="{'has-error': ($v.passwordConfirm.$error || formSubmitted)
                  && (!$v.passwordConfirm.required || !$v.passwordConfirm.sameAsPassword)}">
                <input class="form-control-v2"
                       :placeholder="translate.login.confirmPassword"
                       type="password" v-model.trim="$v.passwordConfirm.$model">
            </span>

            <span v-if="$v.passwordConfirm.$error || formSubmitted">
                <error-component :message="translate.login.passwordRepeat"
                                 v-if="!$v.passwordConfirm.required"></error-component>
                <error-component :message="translate.login.passwordDontMatch"
                                 v-if="!$v.passwordConfirm.sameAsPassword"></error-component>
            </span>
        </div>

        <div class="d-flex flex-column gap-3 mt-3">
            <span class="text-muted small text-center" v-html="termsText"></span>

            <button-with-loading-component class="w-100 btn btn-rebrand-primary-outline btn-rebrand-sm"
                                           type="submit"
                                           :text="translate.home.createAccount"
                                           :loading="loading"
                                           @click.native.prevent="onSubmit"
            ></button-with-loading-component>
        </div>

    </form>
</template>

<script>
import {validationMixin} from 'vuelidate';
import {required, sameAs} from 'vuelidate/lib/validators';
import userRegistrationMixin from "@/components/mixins/userRegistrationMixin";
import countryCodeMixin from "@/components/mixins/countryCodeMixin";
import ButtonWithLoadingComponent from "@/components/global/ButtonWithLoadingComponent";
import regionsUrlMixin from "@/components/mixins/regions/regionsUrlMixin";
import translateMixin from "@/components/mixins/translateMixin";
import {setCookie} from "@/core/utils/helpers/cookieHelper";
import CookiesEnum from "@/core/constants/cookiesEnum";

async function created() {
    this.countryCodeId = await this.fetchCountryCodes();
}

function preparePhoneNumberForPayload() {
    const phoneNumberWithoutLeadingZeros = _.trimStart(this.phoneNumber, '0+');
    const dialCodeWithoutExtraCharacters = this.dialCode.replace(/^\D+/g, '');
    const phoneNumberWithoutCountryDialCode = phoneNumberWithoutLeadingZeros.replace(new RegExp(`^${dialCodeWithoutExtraCharacters}`), '');
    const phoneNumber = phoneNumberWithoutCountryDialCode.replace(this.dialCode, '');

    return `${this.dialCode}${phoneNumber}`;
}

async function submitForm() {
    this.loading = true;

    const isPhone = /^\+?\d+$/.test(this.emailOrPhone);
    const payload = {
        username: this.username,
        password: this.password,
        repeat_password: this.passwordConfirm,
        user_role: 0,
        accept_terms: true,
    };

    if (this.registerVia === 'phone') {
        payload.phoneNumber = preparePhoneNumberForPayload.call(this);
        payload.dialCode = this.dialCode;
    } else {
        payload.email = this.email;
    }

    const [err, response] = await to(
        api.auth.registrationFromPunterModal(payload)
    );

    if (err) {
        this.onFailure(_.get(err, "response.data", undefined));
        return;
    }

    if (_.get(response, 'data.success', false)) {
        const token = _.get(response, 'data.token');

        if (token) {
            setCookie(CookiesEnum.JWT, token);
        }

        this.$emit('successRedirect');
    }
}

export default {
    components: {ButtonWithLoadingComponent},
    mixins: [
        validationMixin,
        userRegistrationMixin,
        countryCodeMixin,
        regionsUrlMixin,
        translateMixin,
    ],
    data() {
        return {
            registerVia: 'phone',
            phoneNumber: '',
            email: '',
        };
    },
    created,
    watch: {
        registerVia(newVal) {
            if (newVal === 'phone') {
                this.email = '';
                this.emailNotValid = false;
            } else {
                this.phoneNumber = '';
                this.phoneNotValid = false;
            }
            this.emailOrPhoneTaken = false;
        },
    },
    methods: {
        submitForm,
    },
    validations() {
        const validations = {
            username: {
                required,
            },
            password: {
                required,
            },
            passwordConfirm: {
                required,
                sameAsPassword: sameAs('password'),
            },
            phoneNumber: {},
            email: {},
        };

        if (this.registerVia === 'phone') {
            validations.phoneNumber = {required};
        } else {
            validations.email = {required};
        }

        return validations;
    },
    computed: {
        dialCode() {
            if (!this.countryCodes.length || !this.countryCodeId) {
                return '';
            }

            const found = this.countryCodes.find(c => c.id === this.countryCodeId);
            return found ? found.dialCode : '';
        },
        termsText() {
            return this.fillTranslateParams(this.translate.home.registrationTermsUrl, {
                termsUrl: this.regionRelativeUrl('/agb/'),
            });
        },
    },
};
</script>
